import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import { useAuth, useMissionRoles, useSkillsMatch, useToast } from "hooks";
import { ADD_MISSION_ROLE, UPDATE_MISSION_ROLE } from "graphql/mutations";
import { MUTATION_TYPE } from "hooks";
import { GET_MISSION_ROLES } from "graphql/queries";
import { PAGES } from "constants/index";
import { ModalForm, MODAL_SIZE } from "components/Modal";
import { PrimaryButton } from "components/Buttons";
import { getLeftColFields, getRightColFields } from "constants/missionRoleFormFields";

/**
 * RoleAddButton
 *
 * @param {Boolean}   show
 * @param {Function}  onClose
 * @param {Object}    data
 * @param {String}    missionId
 * @param {String}    domainId      Domain a new role lands in; an edited role's own domain wins, a client's own domain is the fallback
 * @param {Boolean}   loading
 * @param {Boolean}   isEditing
 * @param {Function}  onCompleted   Called with the mutation result after a successful save
 */
const RoleAddButton = ({
  show,
  onClose,
  data,
  missionId: missionIdProp,
  domainId,
  loading,
  isEditing,
  onCompleted,
}) => {
  const [skills, setSkills] = useState([]);
  const [missionId, setMissionId] = useState(missionIdProp);
  const [roleName, setRoleName] = useState(data?.name);
  const { toast } = useToast();
  const { buildFormData } = useMissionRoles({ skipQuery: true, missionId });
  const { isAdmin, isClient, isTalentSimpleClient: isTalentSimple, domainId: organizationDomainId } = useAuth();
  const { count: skillsMatchCount, loading: skillsLoading } = useSkillsMatch({ skills, skip: isClient });

  useEffect(() => {
    if (missionIdProp !== missionId) {
      setMissionId(missionIdProp);
    }
  }, [missionIdProp, missionId]);

  if (isEditing && loading) return null;

  const formProps = {
    initialValues: buildFormData(data, !data?.state),
    updateId: data?.id,
    parentKeyValue: missionId,
    parentKeyName: "missionId",
    mutationType: isEditing ? MUTATION_TYPE.update : MUTATION_TYPE.add,
    mutationData: {
      mutationName: {
        add: ADD_MISSION_ROLE,
        update: UPDATE_MISSION_ROLE,
      },
      refetchQueries: [GET_MISSION_ROLES],
      refetchAfterMutate: true,
    },
  };

  const handleComplete = (result) => {
    onClose();
    onCompleted?.(result);
    toast.success(<FormattedMessage id={`Missions.RoleAddButton.${isEditing ? `EditSuccess` : `CreateSuccess`}`} />);
  };

  const handleSkillsChange = (name, value, onChange) => {
    setSkills(value);
    onChange(name, value);
  };

  const handleError = (error) => {
    const errors = error.graphQLErrors?.[0]?.extensions?.errors;
    if (errors && errors.length > 0) {
      const statusError = errors.find((err) => err.field === "state");

      if (statusError?.message?.includes("one interview question is required to publish")) {
        toast.error(<FormattedMessage id="Missions.Roles.RoleForm.InterviewPublishError" />);
      }
    }
  };

  const editMessagesPhrase = "Edit";
  const createMessagesPhrase = isClient ? "Request" : "Create";
  const currentPhrase = isEditing ? editMessagesPhrase : createMessagesPhrase;

  return (
    <ModalForm
      data={[
        ...FORM_DATA_LEFT_COL({
          onSkillsChange: handleSkillsChange,
          domainId: data?.domain?.id || domainId || organizationDomainId,
          skills,
          skillsMatchCount,
          skillsLoading,
          isClient,
          isTalentSimpleClient: isTalentSimple,
        }),
        ...FORM_DATA_RIGHT_COL({ setMissionId, isAdmin, isClient, isTalentSimpleClient: isTalentSimple }),
      ]}
      show={show}
      onChange={({ name }) => setRoleName(name)}
      size={MODAL_SIZE.large}
      onClose={onClose}
      title={<FormattedMessage id={`Mission.RoleForm.${currentPhrase}FormTitle`} />}
      description={<FormattedMessage id={`Mission.RoleForm.${currentPhrase}FormDescription`} />}
      onCompleted={handleComplete}
      onError={handleError}
      page={isEditing ? PAGES.editMissionRole : PAGES.addMissionRole}
      primaryButtonMessageId={
        isTalentSimple
          ? "Global.Submit"
          : isEditing
          ? "Global.Save"
          : `Mission.RoleForm.${createMessagesPhrase}Role.CTA`
      }
      footer={
        isTalentSimple && !isEditing
          ? ({ submitPartial, loading: saving }) => (
              <PrimaryButton onClick={() => submitPartial({ isInProgress: true })} disabled={!roleName || saving}>
                <FormattedMessage id="Mission.RoleForm.ContinueLater.CTA" />
              </PrimaryButton>
            )
          : null
      }
      {...formProps}
    />
  );
};

const FORM_DATA_LEFT_COL = (args) => [
  {
    style: css`
      width: 65%;
      padding-right: 4rem !important;
    `,
    items: getLeftColFields(args),
  },
];

const FORM_DATA_RIGHT_COL = (args) => [
  {
    style: css`
      width: 35%;
      padding-left: 4rem !important;
    `,
    items: getRightColFields(args),
  },
];

RoleAddButton.defaultProps = {
  show: false,
  isEditing: false,
};

RoleAddButton.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  data: PropTypes.object,
  missionId: PropTypes.string,
  domainId: PropTypes.string,
  loading: PropTypes.bool,
  isEditing: PropTypes.bool,
  onCompleted: PropTypes.func,
};

export default RoleAddButton;
